import React from 'react';
import PublicNav from '../../components/layout/PublicNav';

/**
 * Privacy Policy Component
 * 
 * Displays the company's privacy policy
 */
const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-[#6C5DD3] to-[#8247E5]">
      <div className="container mx-auto px-4 py-12">
        <div className="max-w-4xl mx-auto bg-white bg-opacity-95 backdrop-blur-sm rounded-3xl shadow-xl overflow-hidden">
          <div className="p-8 md:p-12">
            <PublicNav showPolicyLinks />

            <h1 className="text-3xl font-bold text-[#6C5DD3] mb-6">Privacy Policy</h1>
            
            <div className="prose max-w-none text-gray-600">
              <p className="text-sm text-gray-500 mb-8">Last Updated: April 15, 2024</p>
              
              <section className="mb-6">
                <h2 className="text-xl font-semibold text-gray-700 mb-3">Introduction</h2>
                <p>
                  O Industries LLC ("we", "our", or "us") respects your privacy and is committed to protecting your personal and financial data. 
                  This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use the MnyMny application 
                  and related services (collectively, the "Service").
                </p>
                <p>
                  Please read this Privacy Policy carefully. By using the Service, you agree to the collection and use of information in accordance 
                  with this policy. If you do not agree with our policies and practices, do not use our Service.
                </p>
              </section>
              
              <section className="mb-6">
                <h2 className="text-xl font-semibold text-gray-700 mb-3">Information We Collect</h2>
                
                <h3 className="text-lg font-medium text-gray-700 mb-2">Personal Information</h3>
                <p>We may collect personally identifiable information, such as:</p>
                <ul className="list-disc ml-6 mb-4">
                  <li>Name, email address, and phone number</li>
                  <li>Date of birth</li>
                  <li>Profile information</li>
                  <li>Device information and IP address</li>
                </ul>
                
                <h3 className="text-lg font-medium text-gray-700 mb-2">Financial Information</h3>
                <p>Through our service, we may collect financial information such as:</p>
                <ul className="list-disc ml-6 mb-4">
                  <li>Bank account information (via secure third-party services)</li>
                  <li>Transaction history</li>
                  <li>Income information</li>
                  <li>Budget and expense data</li>
                </ul>
                
                <h3 className="text-lg font-medium text-gray-700 mb-2">Usage Information</h3>
                <p>We automatically collect certain information about how you interact with our Service, including:</p>
                <ul className="list-disc ml-6 mb-4">
                  <li>Login frequency and duration</li>
                  <li>Features and pages accessed</li>
                  <li>Browser or app information</li>
                  <li>Referring source</li>
                </ul>
              </section>
              
              <section className="mb-6">
                <h2 className="text-xl font-semibold text-gray-700 mb-3">How We Use Your Information</h2>
                <p>We use the information we collect for various purposes, including to:</p>
                <ul className="list-disc ml-6 mb-4">
                  <li>Provide, maintain, and improve our Service</li>
                  <li>Process transactions and manage your accounts</li>
                  <li>Send notifications related to your account or activity</li>
                  <li>Respond to your comments and questions</li>
                  <li>Understand how users interact with our Service</li>
                  <li>Personalize your experience and deliver tailored content</li>
                  <li>Detect, prevent, and address technical issues or fraudulent activities</li>
                  <li>Comply with legal obligations</li>
                </ul>
              </section>
              
              <section className="mb-6">
                <h2 className="text-xl font-semibold text-gray-700 mb-3">Data Sharing and Disclosure</h2>
                <p>We do not sell your personal information. We may share your information in the following situations:</p>
                
                <h3 className="text-lg font-medium text-gray-700 mb-2">Service Providers</h3>
                <p>
                  We may share your information with third-party vendors, service providers, and partners who require access to such 
                  information to perform services on our behalf, such as data analysis, payment processing, email delivery, hosting, 
                  or customer service.
                </p>
                
                <h3 className="text-lg font-medium text-gray-700 mb-2">Financial Partners</h3>
                <p>
                  When you connect your financial accounts, we use Plaid to transfer your financial data securely. Plaid's use of your 
                  personal information is governed by their privacy policy available at <a href="https://plaid.com/legal/" target="_blank" rel="noopener noreferrer" className="text-indigo-600 hover:underline">https://plaid.com/legal/</a>.
                </p>
                
                <h3 className="text-lg font-medium text-gray-700 mb-2">Legal Requirements</h3>
                <p>
                  We may disclose your information if required by law, regulation, legal process, or governmental request.
                </p>
                
                <h3 className="text-lg font-medium text-gray-700 mb-2">Business Transfers</h3>
                <p>
                  In the event of a merger, acquisition, or asset sale, your personal information may be transferred. We will notify you 
                  before your information becomes subject to a different privacy policy.
                </p>
              </section>
              
              <section className="mb-6">
                <h2 className="text-xl font-semibold text-gray-700 mb-3">Data Security</h2>
                <p>
                  We implement appropriate technical and organizational measures to protect the security of your personal information. 
                  However, please note that no method of transmission over the Internet or electronic storage is 100% secure.
                </p>
                <p>
                  We use industry-standard encryption, secure server infrastructure, and strict access controls to protect your data. 
                  Financial information is encrypted both in transit and at rest using strong encryption algorithms.
                </p>
              </section>
              
              <section className="mb-6">
                <h2 className="text-xl font-semibold text-gray-700 mb-3">Your Privacy Rights</h2>
                <p>Depending on your location, you may have certain rights regarding your personal information, including:</p>
                <ul className="list-disc ml-6 mb-4">
                  <li>Right to know what personal information we collect</li>
                  <li>Right to request deletion of your personal information</li>
                  <li>Right to access and obtain a copy of your personal information</li>
                  <li>Right to opt-out of the sale of your personal information (note: we do not sell your information)</li>
                  <li>Right to non-discrimination for exercising these rights</li>
                </ul>
                <p>
                  To exercise these rights, please contact us at privacy@oindustries.com. We will respond to your request within 
                  the timeframe required by applicable law.
                </p>
              </section>
              
              <section className="mb-6">
                <h2 className="text-xl font-semibold text-gray-700 mb-3">Children's Privacy</h2>
                <p>
                  Our Service is not intended for children under 18 years of age. We do not knowingly collect personal information from 
                  children under 18. If you are a parent or guardian and believe your child has provided us with personal information, 
                  please contact us so that we can delete such information.
                </p>
              </section>
              
              <section className="mb-6">
                <h2 className="text-xl font-semibold text-gray-700 mb-3">Changes to This Privacy Policy</h2>
                <p>
                  We may update our Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy 
                  on this page and updating the "Last Updated" date at the top of this page. You are advised to review this Privacy Policy 
                  periodically for any changes.
                </p>
              </section>
              
              <section className="mb-6">
                <h2 className="text-xl font-semibold text-gray-700 mb-3">Contact Us</h2>
                <p>
                  If you have any questions about this Privacy Policy, please contact us at:
                </p>
                <p className="mt-2">
                  O Industries LLC<br />
                  Email: privacy@oindustries.com<br />
                  Address: [Company Address]
                </p>
              </section>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy; 